import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import {ConversationRepository} from "../repositories/ConversationRepository";
import {useStompWebsocket} from "../hooks/useStompWebsocket";

export const DashboardContext = createContext({
    responseTime: [],
    statuses: {},
    conversations: [],
    pagination: {page: 0, size: 25, totalElements: 0},
    nodeUsage: {},
    wsState: "inactive",
    updatedValue: null,
    tagUsage: {},
    dateRange: null,
    setDateRange: () => {
    },
    refreshInterval: null,
    setRefreshInterval: () => {
    },
    lastFetched: null,
    refreshResponseTime: () => {
    },
    refreshStatuses: () => {
    },
    refreshConversations: () => {
    },
    refreshNodeUsage: () => {
    },
    refreshTagsUsage: () => {
    },
    refreshAll: () => {
    },
    setPagination: () => {
    },
});

export const DashboardContextProvider = ({project, children}) => {
    const [updatedValue, wsState] = useStompWebsocket(project?.id ? `/topic/flow/${project?.id}` : null);

    const [responseTime, setResponseTime] = useState([]);
    const [statuses, setStatuses] = useState({});
    const [conversations, setConversations] = useState([]);
    const [pagination, setPagination] = useState({page: 0, size: 25, totalElements: 0});
    const [nodeUsage, setNodeUsage] = useState({});
    const [tagUsage, setTagUsage] = useState({});
    // dateRange: {label, start, end} (moment instances) | null — applies to stats only, NOT to the conversations list
    const [dateRange, setDateRange] = useState(null);
    // refreshInterval: milliseconds | null — how often stats (NOT conversations) auto-refetch
    const [refreshInterval, setRefreshInterval] = useState(null);
    // lastFetched: Date | null — when the stats (NOT conversations) were last successfully refreshed
    const [lastFetched, setLastFetched] = useState(null);

    // Fetch response time data
    const refreshResponseTime = useCallback(async (signal) => {
        if (!project?.id) return;

        try {
            const response = await ConversationRepository.getResponseTime({
                flowUid: project.id,
                start: dateRange?.start ? dateRange.start.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                end: dateRange?.end ? dateRange.end.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                signal
            });
            setResponseTime(response?.data || []);
        } catch (error) {
            if (error.name !== "CanceledError" && error.name !== "AbortError") {
                console.error("Error fetching response time:", error);
            }
        }
    }, [project?.id, dateRange]);

    // Fetch status statistics
    const refreshStatuses = useCallback(async (signal) => {
        if (!project?.id) return;

        try {
            const response = await ConversationRepository.getStatusStatistic({
                flowUid: project.id,
                type: project?.settings?.projectType || "VOICE_BOT",
                start: dateRange?.start ? dateRange.start.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                end: dateRange?.end ? dateRange.end.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                signal
            });
            setStatuses(response?.data || {});
        } catch (error) {
            if (error.name !== "CanceledError" && error.name !== "AbortError") {
                console.error("Error fetching statuses:", error);
            }
        }
    }, [project?.id, dateRange]);

    // Fetch conversations — intentionally NOT affected by dateRange
    const refreshConversations = useCallback(async (filters = {}) => {
        if (!project?.id) return;

        try {
            const response = await ConversationRepository.getConversations({
                flow: project.id,
                page: pagination?.page || 0,
                size: pagination?.size || 25,
                type: project?.settings?.projectType || "VOICE_BOT",
                ...filters,
            });

            setPagination(prev => ({
                ...prev,
                totalElements: response?.data?.totalElements || 0
            }));
            setConversations(response?.data?.content || []);
        } catch (error) {
            console.error("Error fetching conversations:", error);
        }
    }, [project?.id, pagination?.page, pagination?.size]);

    // Fetch node usage data
    const refreshNodeUsage = useCallback(async () => {
        if (!project?.id) return;

        const tags = project?.settings?.tags || [];
        const ids = tags?.map(tag => tag?.nodes || [])?.flat(1);

        if (ids.length === 0) {
            setNodeUsage({});
            return;
        }

        try {
            const response = await ConversationRepository.getNodeUsage(ids, {
                start: dateRange?.start ? dateRange.start.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                end: dateRange?.end ? dateRange.end.format("YYYY-MM-DDTHH:mm:ss") : undefined,
            });
            setNodeUsage(response?.data || {});
        } catch (error) {
            console.error("Error fetching node usage:", error);
        }
    }, [project?.id, project?.settings?.tags, dateRange]);

    const refreshTagsUsage = useCallback(async (start, end) => {
        if (!project?.id) return;

        const tags = project?.settings?.tags || [];
        const map = {}
        for (const tag of tags) {
            map[tag.name] = tag.id;
        }

        if (Object.keys(map).length === 0) {
            setTagUsage({});
            return;
        }

        // explicit start/end (e.g. a component's own picker) override the global dateRange
        const rangeStart = start !== undefined ? start : dateRange?.start;
        const rangeEnd = end !== undefined ? end : dateRange?.end;

        try {
            const response = await ConversationRepository.getTagsUsage({
                project_id: project?.id,
                tags: map,
                type: project?.settings?.projectType || "VOICE_BOT",
                start: rangeStart ? rangeStart.format("YYYY-MM-DDTHH:mm:ss") : undefined,
                end: rangeEnd ? rangeEnd.format("YYYY-MM-DDTHH:mm:ss") : undefined
            });
            setTagUsage(response?.data || {});
        } catch (error) {
            console.error("Error fetching tags usage:", error);
        }
    }, [project?.id, project?.settings?.tags, dateRange]);


    // Refresh all data
    const refreshAll = useCallback(async () => {
        const controller = new AbortController();

        await Promise.all([
            refreshResponseTime(controller.signal),
            refreshStatuses(controller.signal),
            refreshConversations(),
            refreshNodeUsage(),
            refreshTagsUsage()
        ]);

        return () => controller.abort();
    }, [refreshResponseTime, refreshStatuses, refreshConversations, refreshNodeUsage, refreshTagsUsage]);

    // Auto-refresh when WebSocket updates
    useEffect(() => {
        if (!project?.id) return;

        const controller = new AbortController();

        Promise.all([
            refreshResponseTime(controller.signal),
            refreshStatuses(controller.signal)
        ]);

        return () => controller.abort();
    }, [project?.id, updatedValue, refreshResponseTime, refreshStatuses]);

    // Re-fetch stats (NOT conversations) whenever the date range changes
    useEffect(() => {
        if (!project?.id) return;

        const controller = new AbortController();

        Promise.all([
            refreshResponseTime(controller.signal),
            refreshStatuses(controller.signal),
            refreshNodeUsage(),
            refreshTagsUsage()
        ]).then(() => setLastFetched(new Date()));

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    // Periodically re-fetch stats (NOT conversations) at the chosen interval
    useEffect(() => {
        if (!project?.id || !refreshInterval) return;

        const intervalId = setInterval(() => {
            const controller = new AbortController();
            Promise.all([
                refreshResponseTime(controller.signal),
                refreshStatuses(controller.signal),
                refreshNodeUsage(),
                refreshTagsUsage()
            ]).then(() => setLastFetched(new Date()));
        }, refreshInterval);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id, refreshInterval, dateRange]);

    const value = {
        responseTime,
        statuses,
        conversations,
        pagination,
        nodeUsage,
        wsState,
        tagUsage,
        updatedValue,
        dateRange,
        setDateRange,
        refreshInterval,
        setRefreshInterval,
        lastFetched,
        refreshResponseTime,
        refreshStatuses,
        refreshConversations,
        refreshNodeUsage,
        refreshTagsUsage,
        refreshAll,
        setPagination,
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboard must be used within DashboardContextProvider");
    }
    return context;
};